/**
 * Connection machine — pure reducer.
 *
 * `(state, event) → { state, effects }` with NO side effects: no sockets, no
 * timers, no React. The driver (`useAssistantConnection`) executes the
 * returned effects. All transport policy lives here so it is exhaustively
 * unit-testable without a single WebSocket mock (see reducer.test.ts).
 *
 * Policy summary:
 * - Intentional session switches (new chat / resume) reconnect IMMEDIATELY;
 *   unexpected drops back off (RECONNECT_DELAY_MS × MAX_RECONNECT_ATTEMPTS)
 *   then go terminal — exact parity with the legacy hook's recovery behavior.
 * - User sends are honored in every phase: written to the wire when OPEN,
 *   queued otherwise, and a send against a FAILED connection revives it
 *   (user intent restarts the retry budget).
 * - Ack lifecycle: un-acked SENT items are ALWAYS re-queued on a socket drop
 *   (including before the first ack — the tab-wake window, where the frames
 *   no-oped against an already-dead socket). Safe because the backend dedups
 *   resends on client_msg_id at MANAGER level, surviving its per-socket
 *   session teardown (aol-us-ai-asst backend `client_msg_dedup_window`) —
 *   at-most-once processing. `serverAcks` (see types.ts) still gates
 *   ACK_TIMEOUT only: a proven-acking backend fails fast on a silent
 *   deadline (explicit Retry); an unproven one waits honestly — items stay
 *   SENT until an ack resolves them or the socket close re-queues them.
 */

import {
  ACK_TIMEOUT_MS,
  ConnectionPhase,
  MAX_RECONNECT_ATTEMPTS,
  OutboundFrameKind,
  RECONNECT_DELAY_MS,
  SendStatus,
  type ConnEffect,
  type ConnEvent,
  type ConnState,
  type OutboxItem,
  type ReducerResult,
} from './types';

// ── Helpers ───────────────────────────────────────────────────────

const statusUpdates = (
  items: OutboxItem[],
  status: SendStatus
): ConnEffect => ({
  type: 'UPDATE_MESSAGE_STATUS',
  updates: items.map((i) => ({ id: i.id, status })),
});

/** Mark items SENT and emit the wire + ack-timer effects. */
function flushItems(state: ConnState, items: OutboxItem[]): ReducerResult {
  const sent = items.map<OutboxItem>((i) => ({
    ...i,
    status: SendStatus.SENT,
    attempts: i.attempts + 1,
  }));
  const untouched = state.outbox.filter(
    (i) => !items.some((f) => f.id === i.id)
  );
  return {
    state: { ...state, outbox: [...untouched, ...sent] },
    effects: [
      { type: 'SEND_FRAMES', items: sent },
      statusUpdates(sent, SendStatus.SENT),
      { type: 'SCHEDULE_ACK_TIMEOUT', delayMs: ACK_TIMEOUT_MS },
    ],
  };
}

/** Open a socket right now (intentional switch, revival, or retry tick). */
const reconnectNow = (state: ConnState): ReducerResult => ({
  state: {
    ...state,
    phase: ConnectionPhase.CONNECTING,
    pendingSwitch: false,
  },
  effects: [{ type: 'OPEN_SOCKET' }],
});

// ── Reducer ───────────────────────────────────────────────────────

export function connectionReducer(
  state: ConnState,
  event: ConnEvent
): ReducerResult {
  switch (event.type) {
    case 'CONNECT_REQUESTED': {
      // Idempotent: only meaningful from IDLE (mount). A duplicate request
      // while live is a no-op rather than a socket leak.
      if (state.phase !== ConnectionPhase.IDLE) return { state, effects: [] };
      return reconnectNow(state);
    }

    case 'SOCKET_OPENED': {
      // Fresh reconnect budget after any successful open (legacy parity),
      // and the pending switch (if any) has landed.
      const opened: ConnState = {
        ...state,
        phase: ConnectionPhase.OPEN,
        attempts: 0,
        pendingSwitch: false,
      };
      const queued = opened.outbox.filter(
        (i) => i.status === SendStatus.QUEUED
      );
      if (queued.length === 0) return { state: opened, effects: [] };
      // Drain the outbox. The driver has already sent SETTINGS (the backend
      // requires it as the literal first frame), so these flush strictly after.
      return flushItems(opened, queued);
    }

    case 'SOCKET_CLOSED': {
      // Intentional switch: SESSION_CHANGED tore the socket down — reconnect
      // immediately under the new session, no backoff, no attempt consumed.
      if (state.pendingSwitch) {
        return reconnectNow({ ...state, attempts: 0 });
      }
      // Terminal phase closes (e.g. the failed socket's own close event
      // straggling in) change nothing.
      if (
        state.phase === ConnectionPhase.FAILED ||
        state.phase === ConnectionPhase.IDLE
      ) {
        return { state, effects: [] };
      }

      // Unexpected drop. Un-acked SENT items were written to a dying socket —
      // ALWAYS re-queue them, even before any ack has been observed this
      // mount (serverAcks === null). The null case IS the tab-wake window:
      // machine still OPEN, socket already dead, onclose not yet fired — the
      // first send's frames no-oped against the dead socket, so the old
      // "assume delivered; resolve ACKED" policy turned a delivered-looking
      // bubble into a silently dropped message that nothing ever resent.
      // Re-queueing is safe because the backend provably acks every
      // client_msg_id-stamped frame and dedups resends at MANAGER level —
      // the window deliberately survives its own per-socket session teardown
      // (aol-us-ai-asst backend/main.py `_ack_and_dedup_text_frame` +
      // manager.py `client_msg_dedup_window`) — so a resend of a frame the
      // server did receive is re-acked, never re-processed (at-most-once).
      const sentItems = state.outbox.filter(
        (i) => i.status === SendStatus.SENT
      );
      const effects: ConnEffect[] = [];
      let outbox = state.outbox;
      if (sentItems.length > 0) {
        outbox = outbox.map((i) =>
          i.status === SendStatus.SENT ? { ...i, status: SendStatus.QUEUED } : i
        );
        effects.push(statusUpdates(sentItems, SendStatus.QUEUED));
      }

      // Retry budget check mirrors the legacy onclose exactly.
      if (state.attempts >= MAX_RECONNECT_ATTEMPTS) {
        const undelivered = outbox; // everything left is queued
        return {
          state: {
            ...state,
            phase: ConnectionPhase.FAILED,
            outbox: [],
          },
          effects: [
            ...effects,
            ...(undelivered.length > 0
              ? [statusUpdates(undelivered, SendStatus.FAILED)]
              : []),
            { type: 'NOTIFY_TERMINAL' },
          ],
        };
      }
      return {
        state: {
          ...state,
          phase: ConnectionPhase.BACKOFF,
          attempts: state.attempts + 1,
          outbox,
        },
        effects: [
          ...effects,
          { type: 'SCHEDULE_RETRY', delayMs: RECONNECT_DELAY_MS },
        ],
      };
    }

    case 'RETRY_TICK': {
      if (state.phase !== ConnectionPhase.BACKOFF)
        return { state, effects: [] };
      return reconnectNow(state);
    }

    case 'SEND': {
      const item: OutboxItem = {
        id: event.id,
        text: event.text,
        attempts: 0,
        status: SendStatus.QUEUED,
        frameKind: event.frameKind ?? OutboundFrameKind.TEXT,
        handoffId: event.handoffId,
      };
      if (state.phase === ConnectionPhase.OPEN) {
        return flushItems(state, [
          // flushItems marks SENT; stage it into the outbox first
          item,
        ]);
      }
      const queuedState: ConnState = {
        ...state,
        outbox: [...state.outbox, item],
      };
      const effects: ConnEffect[] = [statusUpdates([item], SendStatus.QUEUED)];
      // User intent revives a terminal connection: typing after "lost
      // connection" restarts the retry budget instead of dead-ending.
      if (state.phase === ConnectionPhase.FAILED) {
        return {
          state: {
            ...queuedState,
            phase: ConnectionPhase.CONNECTING,
            attempts: 0,
          },
          effects: [...effects, { type: 'OPEN_SOCKET' }],
        };
      }
      // CONNECTING/BACKOFF/IDLE: the queued item drains on the next open.
      return { state: queuedState, effects };
    }

    case 'ACK_RECEIVED': {
      const item = state.outbox.find(
        (i) => i.id === event.id && i.status === SendStatus.SENT
      );
      // Capability proven either way — an ack for an already-resolved item
      // (duplicate re-ack from the backend dedup) still tells us the server acks.
      const next: ConnState = { ...state, serverAcks: true };
      if (!item) return { state: next, effects: [] };
      return {
        state: {
          ...next,
          outbox: next.outbox.filter((i) => i.id !== event.id),
        },
        effects: [statusUpdates([item], SendStatus.ACKED)],
      };
    }

    case 'ACK_TIMEOUT': {
      const unacked = state.outbox.filter((i) => i.status === SendStatus.SENT);
      if (unacked.length === 0) return { state, effects: [] };
      const remaining = state.outbox.filter(
        (i) => i.status !== SendStatus.SENT
      );
      if (state.serverAcks === true) {
        // Proven ack-capable backend stayed silent past the deadline on a
        // healthy socket — treat as lost; the user gets an explicit Retry.
        return {
          state: { ...state, outbox: remaining },
          effects: [statusUpdates(unacked, SendStatus.FAILED)],
        };
      }
      // Unproven backend: HONEST WAITING — do nothing. Items stay SENT
      // ("Sending…") so a later ACK_RECEIVED resolves them or SOCKET_CLOSED
      // re-queues them for the reconnect reflush. The old behavior here
      // (resolve optimistically as ACKED) is what let the tab-wake drop
      // survive: an OS-frozen socket's close event can take tens of seconds,
      // so the 5s timeout fired first, removed the items, and the eventual
      // close found nothing to re-queue — a "delivered" bubble for a message
      // the backend never saw. Residual (documented, accepted): against a
      // backend that NEVER acks, the bubble sits at "Sending…" until the
      // socket closes — no such backend is deployed in any environment
      // (see serverAcks in types.ts).
      return { state, effects: [] };
    }

    case 'RETRY_MESSAGE': {
      // Same shape as SEND but reuses the original id — the backend dedup
      // key stays stable, so a retry of a frame the server actually received
      // is re-acked, never re-processed (get_or_create semantics). Passes
      // through frameKind/handoffId so retrying a handoff reply re-sends as
      // agent_text, not a plain Sara text frame.
      return connectionReducer(state, {
        type: 'SEND',
        id: event.id,
        text: event.text,
        frameKind: event.frameKind,
        handoffId: event.handoffId,
      });
    }

    case 'SESSION_CHANGED': {
      // Never leak a message typed against conversation A into conversation B.
      const base: ConnState = { ...state, outbox: [], attempts: 0 };
      if (
        state.phase === ConnectionPhase.OPEN ||
        state.phase === ConnectionPhase.CONNECTING
      ) {
        // Live socket: close it; the resulting SOCKET_CLOSED sees
        // pendingSwitch and reconnects immediately.
        return {
          state: { ...base, pendingSwitch: true },
          effects: [{ type: 'CLOSE_SOCKET', reason: 'Session changed' }],
        };
      }
      // No live socket (backoff/failed/idle): open directly. The driver
      // cancels any pending retry timer when executing OPEN_SOCKET.
      return reconnectNow(base);
    }
  }
}

// ── Presentation projection ───────────────────────────────────────

/** Map machine phase → the badge's ConnectionStatus string. BACKOFF reads as
 *  'connecting' (legacy parity: the badge showed "Connecting…" during the
 *  retry window); FAILED is the terminal 'disconnected'. The server-driven
 *  upstream RECONNECTING overlay is applied by the driver on top of this. */
export function phaseToConnectionStatus(
  phase: ConnectionPhase
): 'connecting' | 'connected' | 'disconnected' {
  switch (phase) {
    case ConnectionPhase.OPEN:
      return 'connected';
    case ConnectionPhase.FAILED:
      return 'disconnected';
    case ConnectionPhase.IDLE:
    case ConnectionPhase.CONNECTING:
    case ConnectionPhase.BACKOFF:
      return 'connecting';
  }
}
